import { useEffect, useRef, useCallback } from 'react';
import { useAppStore } from '../store/useAppStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase, isAuthConfigured } from '../lib/supabase';
import { SSELeaderElection } from '../lib/sseLeader';
import type { LiveGameData, Alert, AgentActivityEvent, IntelligenceSignal } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { queryClient } from '../lib/queryClient';

// ── Device identity ────────────────────────────────────────────────────────
// Stable per-tab ID stored in sessionStorage. Lets the backend log which
// device/tab opened a connection without persisting across tabs.
const DEVICE_ID_KEY = 'navyrx.device_id';

function getDeviceId(): string {
  let id = sessionStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = uuidv4();
    sessionStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

// ── Event deduplication ────────────────────────────────────────────────────
// Prevents the same logical event from being applied twice when a reconnect
// races with BroadcastChannel delivery. Tracks the last MAX_DEDUP_CACHE IDs
// in insertion order (oldest evicted first).
const processedEventIds = new Set<string>();
const MAX_DEDUP_CACHE = 200;

function markProcessed(id: string): boolean {
  if (processedEventIds.has(id)) return false;
  processedEventIds.add(id);
  if (processedEventIds.size > MAX_DEDUP_CACHE) {
    const oldest = processedEventIds.values().next().value;
    if (oldest) processedEventIds.delete(oldest);
  }
  return true;
}

// ── SSE hook ───────────────────────────────────────────────────────────────

export function useSSE(gamePk?: number) {
  const esRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const lastEventIdRef = useRef<string | null>(null);
  const leaderRef = useRef<SSELeaderElection | null>(null);

  // Batch buffer for agent_activity events — flushes every 300ms to avoid
  // per-event re-renders during high-frequency agent bursts.
  const agentBufRef = useRef<AgentActivityEvent[]>([]);
  const agentFlushRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    updateLiveGame,
    addAlert,
    addAgentActivity,
    addSignal,
    setPulseScore,
    setMarketVolatility,
    fetchGames,
    fetchRecommendations,
    setSSEStatus,
  } = useAppStore();

  /**
   * Processes a named SSE event payload. Used by:
   * - The leader tab (events received from EventSource)
   * - Follower tabs (events received from BroadcastChannel)
   * Deduplication is applied here so both paths are protected.
   */
  const processEvent = useCallback((name: string, rawData: unknown) => {
    switch (name) {
      case 'live_update': {
        updateLiveGame(rawData as LiveGameData);
        break;
      }
      case 'slate_refresh': {
        fetchGames(useAppStore.getState().selectedDate);
        void queryClient.invalidateQueries({ queryKey: ['games'] });
        break;
      }
      case 'new_recommendations': {
        fetchRecommendations(useAppStore.getState().selectedDate);
        void queryClient.invalidateQueries({ queryKey: ['recommendations'] });
        break;
      }
      case 'alert': {
        const raw = rawData as Partial<Alert> & { id?: string | number };
        const dedupeKey = `alert:${String(raw.id ?? Date.now())}`;
        if (!markProcessed(dedupeKey)) return;
        addAlert({
          id: Number(raw.id ?? Date.now()),
          type: raw.type!,
          title: raw.title!,
          body: raw.body!,
          gamePk: raw.gamePk,
          severity: raw.severity ?? 'info',
          is_read: 0,
          created_at: raw.created_at ?? new Date().toISOString(),
        });
        break;
      }
      case 'agent_activity': {
        const raw = rawData as Partial<AgentActivityEvent>;
        const id = raw.id ?? uuidv4();
        if (!markProcessed(`agent:${id}`)) return;
        agentBufRef.current.push({
          id,
          agent: raw.agent ?? 'unknown',
          action: raw.action ?? '',
          gamePk: raw.gamePk,
          module: raw.module ?? 'mlb',
          leagueId: (raw as AgentActivityEvent).leagueId,
          confidenceDelta: raw.confidenceDelta,
          timestamp: raw.timestamp ?? new Date().toISOString(),
          severity: raw.severity ?? 'info',
        });
        if (agentFlushRef.current) clearTimeout(agentFlushRef.current);
        agentFlushRef.current = setTimeout(() => {
          const batch = agentBufRef.current.splice(0);
          batch.forEach(addAgentActivity);
        }, 300);
        break;
      }
      case 'signal_fired': {
        const raw = rawData as Partial<IntelligenceSignal>;
        const id = raw.id ?? uuidv4();
        if (!markProcessed(`signal:${id}`)) return;
        addSignal({
          id,
          type: raw.type ?? 'ai_confidence_update',
          title: raw.title ?? '',
          description: raw.description ?? '',
          selection: raw.selection,
          odds: raw.odds,
          movement: raw.movement,
          gamePk: raw.gamePk,
          module: raw.module ?? 'mlb',
          leagueId: raw.leagueId,
          subProductId: raw.subProductId,
          severity: raw.severity ?? 'info',
          timestamp: raw.timestamp ?? new Date().toISOString(),
          isActive: raw.isActive ?? true,
        });
        break;
      }
      case 'market_movement': {
        const raw = rawData as { volatility?: string; id?: string };
        if (raw.id && !markProcessed(`mkt:${raw.id}`)) return;
        const v = raw.volatility;
        if (v === 'low' || v === 'mid' || v === 'high') setMarketVolatility(v);
        break;
      }
      case 'pulse_score_update': {
        const raw = rawData as { score?: number };
        if (typeof raw.score === 'number') setPulseScore(raw.score);
        break;
      }
    }
  }, [updateLiveGame, addAlert, addAgentActivity, addSignal, setPulseScore,
      setMarketVolatility, fetchGames, fetchRecommendations]);

  const connect = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    const params = new URLSearchParams();
    if (gamePk) params.set('gamePk', String(gamePk));

    // EventSource cannot send custom headers — auth is passed as query params.
    // The backend promotes them to headers before running getRequestContext().
    const token = useAuthStore.getState().session?.access_token;
    if (token) params.set('token', token);
    const orgId = useAuthStore.getState().organizationId;
    if (orgId) params.set('orgId', orgId);

    params.set('deviceId', getDeviceId());

    // Advertise the last received event ID so the server can replay missed
    // events on reconnect (requires server-side replay buffer).
    if (lastEventIdRef.current) params.set('lastEventId', lastEventIdRef.current);

    const es = new EventSource(`/api/events?${params.toString()}`);
    esRef.current = es;
    setSSEStatus('connecting');

    const handleEvent = (e: MessageEvent, eventName: string) => {
      // Track the server-assigned event ID for reconnect replay
      const msgWithId = e as MessageEvent & { lastEventId?: string };
      if (msgWithId.lastEventId) lastEventIdRef.current = msgWithId.lastEventId;

      try {
        const data = JSON.parse(e.data) as unknown;
        processEvent(eventName, data);
        // Fan-out to follower tabs via BroadcastChannel
        leaderRef.current?.broadcastEvent(eventName, data);
      } catch {
        /* malformed JSON — ignore */
      }
    };

    es.addEventListener('connected', (e) => {
      reconnectAttemptRef.current = 0;
      setSSEStatus('connected');
      // Sync server clock offset if needed (ts is in connected payload)
      try { void JSON.parse((e as MessageEvent).data); } catch { /* ok */ }
    });

    for (const name of [
      'live_update', 'slate_refresh', 'new_recommendations', 'alert',
      'agent_activity', 'signal_fired', 'market_movement', 'pulse_score_update',
    ]) {
      es.addEventListener(name, (e) => handleEvent(e as MessageEvent, name));
    }

    es.onerror = () => {
      setSSEStatus('disconnected');
      // Use a larger base backoff when the tab is hidden (user isn't watching)
      // to reduce reconnect pressure on the server.
      const isHidden = document.visibilityState === 'hidden';
      const base = isHidden ? 5_000 : 1_000;
      const delay = Math.min(base * 2 ** reconnectAttemptRef.current, 60_000);
      reconnectAttemptRef.current += 1;
      // ±20% jitter prevents thundering-herd reconnects after a server restart
      const jittered = delay * (0.8 + Math.random() * 0.4);
      reconnectTimerRef.current = setTimeout(connect, jittered);
    };
  }, [gamePk, processEvent, setSSEStatus]);

  useEffect(() => {
    // ── Leader election ──────────────────────────────────────────────────
    // Only the leader tab opens an EventSource. Follower tabs receive events
    // via BroadcastChannel, producing exactly one backend connection per user
    // regardless of how many tabs are open.
    const leader = new SSELeaderElection(
      () => connect(),                          // onBecomeLeader
      (name, data) => processEvent(name, data), // onReceiveEvent (follower path)
    );
    leaderRef.current = leader;

    // ── Token refresh reconnect ──────────────────────────────────────────
    // Supabase auto-refreshes the access token every ~55 minutes. Without this
    // listener, the EventSource URL would carry a stale token until the next
    // forced reconnect, causing auth drift on long-running sessions.
    const { data: { subscription: authSub } } = isAuthConfigured
      ? supabase.auth.onAuthStateChange((event) => {
          if (event === 'TOKEN_REFRESHED' && leader.isLeader) {
            connect();
          }
        })
      : { data: { subscription: null } };

    // ── Visibility-aware reconnect ───────────────────────────────────────
    // When the user switches back to the tab after a long absence (sleep/wake,
    // mobile app switch), the connection may be stale. Reconnect immediately.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && leader.isLeader) {
        if (!esRef.current || esRef.current.readyState === EventSource.CLOSED) {
          reconnectAttemptRef.current = 0;
          connect();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (agentFlushRef.current) clearTimeout(agentFlushRef.current);
      esRef.current?.close();
      esRef.current = null;
      leader.destroy();
      leaderRef.current = null;
      authSub?.unsubscribe();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      setSSEStatus('disconnected');
    };
  }, [connect, processEvent, setSSEStatus]);
}
